import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Link,
  Grid,
  IconButton,
  FormControl,
  Typography,
  InputAdornment,
  Alert,
  Collapse,
  Container,
  TextField,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailTwoToneIcon from '@mui/icons-material/EmailTwoTone';
import LockTwoToneIcon from '@mui/icons-material/LockTwoTone';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { AppRoutes } from '../axios/endPoints';
import Loader from '../components/common/Loader';
import { useEffect, useState } from 'react';
import { EndPoints } from '../axios/endPoints';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../axios/axiosHandler';
import '../styles/Login.css';

const schema = (t: Function) =>
  yup.object().shape({
    email: yup.string().email(),
    password: yup.string().required(t('RESET_PASSWORD_REQUIRED')),
    passwordConfirmation: yup
      .string()
      .required()
      .oneOf([yup.ref('password'), ''], t('RESET_PASSWORD_MATCH_REQUIRED')),
  });

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { control, handleSubmit, formState } = useForm({
    resolver: yupResolver(schema(t)),
    defaultValues: {
      email: searchParams.get('email') ?? '',
      password: '',
      passwordConfirmation: '',
    },
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetPassword = (data: any) => {
    if (error) return;
    setLoading(true);

    axiosInstance
      .post(EndPoints.RESET_PASSWORD, {
        email: data.email,
        password: data.password,
        token: searchParams.get('token'),
      })
      .then((payload) => {
        setLoading(false);
        setSuccess(payload.data.success);
        setError(payload.data.message);
      });
  };

  const passwordRecovered = () => (
    <Box sx={{ mt: 2, width: '100%' }}>
      <Typography component="p">
        {t('RESET_PASSWORD_SUCCESS_TITLE')}
      </Typography>
      <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
        <Link href={AppRoutes.LOG_IN} variant="body2">
          {t('RESET_PASSWORD_LOGIN_BUTTON')}
        </Link>
      </Grid>
    </Box>
  );

  const resetPasswordForm = () => (
    <Box
      component="form"
      onSubmit={handleSubmit(resetPassword)}
      sx={{ mt: 1, width: '100%' }}
    >
      <Controller
        control={control}
        name="email"
        render={({ field }) => (
          <TextField
            {...field}
            disabled
            margin="normal"
            fullWidth
            placeholder={t('RESET_PASSWORD_EMAIL_ADDRESS')}
            variant="outlined"
            type="email"
            error={!!formState.errors.email}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <EmailTwoToneIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="password"
        render={({ field }) => (
          <TextField
            {...field}
            placeholder={t('RESET_PASSWORD_TEXTFIELD')}
            variant="outlined"
            type="password"
            margin="normal"
            fullWidth
            error={!!formState.errors.password}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <LockTwoToneIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="passwordConfirmation"
        render={({ field }) => (
          <TextField
            {...field}
            placeholder= {t('RESET_PASSWORD_CONFIRM')}
            variant="outlined"
            type="password"
            margin="normal"
            fullWidth
            helperText={formState.errors.passwordConfirmation?.message}
          error={!!formState.errors.passwordConfirmation}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <LockTwoToneIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <Collapse in={error != null}>
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setError(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          severity="error"
        >
          {t(`${error}`)}
        </Alert>
      </Collapse>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        className="button-color"
        sx={{ mt: 3, mb: 2 }}
      >
        {t('RESET_PASSWORD_SET_NEW_PASSWORD_BUTTON')}
      </Button>
    </Box>
  );

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold" mb={1}>
          {t('RESET_PASSWORD_TITLE')}
        </Typography>
        <Loader open={loading} />
        {!success ? resetPasswordForm() : passwordRecovered()}
      </Box>
    </Container>
  );
}